import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useAccount, useConnect } from 'wagmi';
import { injected } from 'wagmi/connectors';

import { useSession } from '../hooks/useSession';
import { EscrowMeterLarge } from '../components/EscrowMeter';

/** A whole reply window compressed into 40 seconds, looping. */
function useDemoWindow() {
  const [t, setT] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setT((v) => (v <= 0 ? 1 : v - 0.01)), 400);
    return () => clearInterval(id);
  }, []);
  const hoursLeft = Math.round(t * 48);
  return {
    remaining: t,
    label: hoursLeft > 0 ? `${hoursLeft}h left to reply` : 'returned to sender',
  };
}

export function Landing() {
  const { profile, loading, signIn, error } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const solana = useWallet();
  const { setVisible } = useWalletModal();
  const evm = useAccount();
  const { connect } = useConnect();
  const demo = useDemoWindow();

  if (loading) return <div className="page" />;
  if (profile) return <Navigate to="/inbox" replace />;

  /** One button per chain: connect if needed, then sign. */
  async function enter(chain: 'solana' | 'ethereum') {
    setBusy(chain);
    try {
      if (chain === 'solana' && !solana.publicKey) {
        setVisible(true);
        return;
      }
      if (chain === 'ethereum' && !evm.address) {
        connect({ connector: injected() });
        return;
      }
      await signIn(chain);
    } catch {
      // surfaced through `error`
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <section className="hero">
        <div>
          <h1>
            Write to any wallet.
            <br />
            Get paid to write back.
          </h1>
          <p className="lede">
            Put a price on your inbox and strangers have to stake it before they can reach
            you. Reply and it's yours. Ignore it and every cent goes back to them.
          </p>

          <div className="signin">
            <button
              className="btn btn-primary"
              disabled={busy !== null}
              onClick={() => void enter('solana')}
            >
              {solana.publicKey ? 'Sign in with Solana' : 'Connect a Solana wallet'}
            </button>
            <button className="btn" disabled={busy !== null} onClick={() => void enter('ethereum')}>
              {evm.address ? 'Sign in with Ethereum' : 'Connect an Ethereum wallet'}
            </button>
          </div>

          {error && <p className="hint hint-warn signin-note">{error}</p>}
          <p className="hint signin-note">Signing is free and creates no transaction.</p>
        </div>

        {/* The mechanic, demonstrated rather than asserted. */}
        <div className="demo">
          <span className="eyebrow">A message in escrow</span>
          <div style={{ marginTop: 14 }}>
            <EscrowMeterLarge
              amountMicros="25000000"
              remaining={demo.remaining}
              label={demo.label}
            />
          </div>
          <div className="demo-caption">
            <p className="hint" style={{ margin: 0 }}>
              The bar is the sender's reply window draining. Answer before it empties and the
              money is yours. Let it run out and the contract returns it to the wallet that
              sent it — the only place it can go.
            </p>
          </div>
        </div>
      </section>

      <div className="principles">
        <div className="principle">
          <h3>Addresses, nothing more</h3>
          <p>
            You see the wallet you're talking to and that's all. No names, no emails, no
            history beyond the thread itself.
          </p>
        </div>
        <div className="principle">
          <h3>A price on your attention</h3>
          <p>
            Set what a stranger pays to open a thread. The fee is locked on-chain the moment
            they send, so the offer is real before you spend a second on it.
          </p>
        </div>
        <div className="principle">
          <h3>Silence costs them nothing</h3>
          <p>
            The sender chooses how long you have. Miss the window and their fee goes back to
            them automatically. We never hold it, and we cannot keep it.
          </p>
        </div>
      </div>

      <hr className="rule" />

      <section className="reach">
        <div>
          <h2>You don't have to live here</h2>
          <p className="lede" style={{ fontSize: '0.98rem' }}>
            Link Telegram or Discord and we'll message you there the moment a wallet writes to
            you, with who sent it and how much they staked. Nothing to install, and you can
            unlink at any time.
          </p>
        </div>

        <ul className="channels">
          <li className="channel">
            <span className="channel-name">Telegram</span>
            <span className="hint">A bot DMs you.</span>
          </li>
          <li className="channel">
            <span className="channel-name">Discord</span>
            <span className="hint">The same nudge, as a DM.</span>
          </li>
          <li className="channel is-muted">
            <span className="channel-name">Nothing at all</span>
            <span className="hint">Messages wait for your next visit.</span>
          </li>
        </ul>

        <p className="reach-note">
          Whichever you pick is yours alone. The person messaging you never learns which
          account you linked — or whether you linked one at all.
        </p>
      </section>

      <hr className="rule" />

      <h2 style={{ marginBottom: 18 }}>How a paid message settles</h2>
      <div className="steps">
        <div className="step">
          <span className="step-n">01</span>
          <p>
            The sender locks the fee in an escrow contract on Solana or Ethereum, bound by
            hash to the exact message they wrote.
          </p>
        </div>
        <div className="step">
          <span className="step-n">02</span>
          <p>
            It lands in your inbox here, and — if you've linked Telegram or Discord — a DM
            tells you it arrived.
          </p>
        </div>
        <div className="step">
          <span className="step-n">03</span>
          <p>
            Reply in time and the contract pays you, less our 5%. Stay silent and it goes
            back to the sender in full — we take nothing.
          </p>
        </div>
      </div>
    </div>
  );
}
